import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { CanonicalVariableOption, fieldLabel } from 'src/services/whatsapp/bindings'
import { bindingSourceEnum } from 'src/services/whatsapp/models'
import { cn } from 'src/utils/style'

const SOURCE_LABEL_KEY: Record<CanonicalVariableOption['source'], string> = {
  [bindingSourceEnum.CONTACT]: 'whatsappTemplates.sourceContact',
  [bindingSourceEnum.RESERVATION]: 'whatsappTemplates.sourceReservation',
  [bindingSourceEnum.GUEST]: 'whatsappTemplates.sourceGuest',
  [bindingSourceEnum.ORGANIZATION]: 'whatsappTemplates.sourceOrganization',
  [bindingSourceEnum.RESIDENCE]: 'whatsappTemplates.sourceProperty',
}

/** `options` in groups keyed by source, each group keeping the options' order. */
function groupBySource(
  options: CanonicalVariableOption[],
): { source: CanonicalVariableOption['source']; options: CanonicalVariableOption[] }[] {
  const groups: {
    source: CanonicalVariableOption['source']
    options: CanonicalVariableOption[]
  }[] = []
  options.forEach((option) => {
    const group = groups.find((g) => g.source === option.source)
    if (group) group.options.push(option)
    else groups.push({ source: option.source, options: [option] })
  })
  return groups
}

interface TemplateVariablePickerProps {
  options: CanonicalVariableOption[]
  activeIndex: number
  onSelect: (option: CanonicalVariableOption) => void
  id: string
}

/**
 * Floating, grouped list of canonical `{{source.field}}` tokens — opened by
 * `TemplateBodyInput` while the admin types `{{` in a template body. Purely
 * presentational: the parent owns open/filter/keyboard-nav state and only
 * hands this the already-filtered options and which one is active.
 */
export const TemplateVariablePicker: FC<TemplateVariablePickerProps> = ({
  options,
  activeIndex,
  onSelect,
  id,
}) => {
  const { t } = useTranslation()
  if (options.length === 0) return null
  const groups = groupBySource(options)

  return (
    <div
      id={id}
      role="listbox"
      aria-label={t('whatsappTemplates.variablePickerLabel')}
      data-testid="variable-picker"
      className="absolute z-dropdown mt-1 max-h-60 w-72 overflow-auto rounded-md border border-border bg-surface py-1 shadow-lg"
    >
      {groups.map((group) => (
        <div key={group.source}>
          <p className="px-2 pt-1.5 text-xs font-semibold uppercase tracking-wide text-text-tertiary">
            {t(SOURCE_LABEL_KEY[group.source])}
          </p>
          {group.options.map((option) => {
            const index = options.indexOf(option)
            return (
              <div
                key={option.token}
                id={`${id}-option-${index}`}
                role="option"
                aria-selected={index === activeIndex}
                data-testid={`variable-picker-option-${option.token}`}
                // Fires on mousedown (not click), with the default prevented, so
                // selection runs before the textarea's native blur would otherwise
                // close this list out from under the click.
                onMouseDown={(e) => {
                  e.preventDefault()
                  onSelect(option)
                }}
                className={cn(
                  'flex cursor-pointer items-center justify-between gap-2 px-2 py-1.5 text-sm',
                  index === activeIndex ? 'bg-primary-100 text-primary' : 'text-text-primary',
                )}
              >
                <span className="truncate font-mono text-xs text-primary">{`{{${option.token}}}`}</span>
                <span className="truncate text-text-secondary">{fieldLabel(option.field, t)}</span>
              </div>
            )
          })}
        </div>
      ))}
    </div>
  )
}
